import {
  BoxGeometry,
  BufferGeometry,
  Color,
  DynamicDrawUsage,
  Float32BufferAttribute,
  Group,
  InstancedMesh,
  MeshStandardMaterial,
  Object3D,
} from "three";

import {
  MAPPED_ROAD_VEHICLE_FAMILY_PROFILES,
  MAPPED_ROAD_VEHICLE_STREAMING,
} from "../../config/vehicles.ts";
import type {
  RuntimeParkedVehicleDescriptor,
  RuntimeTileLodLevel,
  RuntimeWorldTilePayloadV2,
} from "../../world/schema/runtimeTile.ts";
import type { WorldTileId } from "../../world/schema/tile.ts";

interface AttachedParkedVehicleTile {
  readonly origin: Readonly<{ x: number; z: number }>;
  readonly descriptors: readonly RuntimeParkedVehicleDescriptor[];
  lod: RuntimeTileLodLevel;
}

interface WorldParkedVehicleDescriptor {
  readonly descriptor: RuntimeParkedVehicleDescriptor;
  readonly x: number;
  readonly z: number;
}

/**
 * Cheap visual tier for the complete mapped parking field. Cars the player has
 * used are suppressed here and replaced by RoadVehicleViewController; merely
 * activating interaction physics does not duplicate their visual hierarchy.
 * Four shared geometries keep the untouched citywide field to four draw calls.
 */
export class ParkedVehicleFieldViewController {
  readonly object = new Group();
  readonly #tiles = new Map<WorldTileId, AttachedParkedVehicleTile>();
  #suppressedIds: ReadonlySet<string> = new Set();
  #focus: Readonly<{ x: number; z: number }> | null = null;
  #meshes: InstancedMesh<BufferGeometry, MeshStandardMaterial>[] = [];

  constructor() {
    this.object.name = "mapped-parked-vehicle-field";
  }

  attachTile(
    tile: Readonly<RuntimeWorldTilePayloadV2>,
    lod: RuntimeTileLodLevel,
  ): void {
    const previous = this.#tiles.get(tile.id);
    const attached = {
      origin: tile.origin,
      descriptors: tile.parkedVehicles ?? [],
      lod,
    };
    this.#tiles.set(tile.id, attached);
    if (
      (previous !== undefined && this.#tileContributes(previous)) ||
      this.#tileContributes(attached)
    ) {
      this.#rebuild();
    }
  }

  setTileLod(tileId: WorldTileId, lod: RuntimeTileLodLevel): void {
    const tile = this.#tiles.get(tileId);
    if (tile === undefined || tile.lod === lod) return;
    tile.lod = lod;
    // Once focus-aware range filtering is active, mapped cars deliberately
    // span tile LODs; a terrain/building LOD swap does not require reallocating
    // their four instanced meshes.
    if (this.#focus === null) this.#rebuild();
  }

  detachTile(tileId: WorldTileId): boolean {
    const tile = this.#tiles.get(tileId);
    if (tile === undefined) return false;
    const contributed = this.#tileContributes(tile);
    this.#tiles.delete(tileId);
    if (contributed) this.#rebuild();
    return true;
  }

  setSuppressedVehicleIds(vehicleIds: ReadonlySet<string>): void {
    if (sameIds(this.#suppressedIds, vehicleIds)) return;
    this.#suppressedIds = new Set(vehicleIds);
    this.#rebuild();
  }

  setFocus(focus: Readonly<{ x: number; z: number }>): void {
    if (
      this.#focus !== null &&
      Math.hypot(focus.x - this.#focus.x, focus.z - this.#focus.z) <
        MAPPED_ROAD_VEHICLE_STREAMING.visualFocusRefreshDistanceM
    ) {
      return;
    }
    this.#focus = { x: focus.x, z: focus.z };
    this.#rebuild();
  }

  clear(): void {
    this.#tiles.clear();
    this.#suppressedIds = new Set();
    this.#disposeMeshes();
  }

  dispose(): void {
    this.clear();
  }

  get visibleVehicleCount(): number {
    return this.#meshes[0]?.count ?? 0;
  }

  #tileContributes(tile: Readonly<AttachedParkedVehicleTile>): boolean {
    if (this.#focus === null && tile.lod !== 0) return false;
    const visualRadiusSquared =
      MAPPED_ROAD_VEHICLE_STREAMING.visualActivationRadiusM ** 2;
    return tile.descriptors.some((descriptor) => {
      if (this.#suppressedIds.has(descriptor.id)) return false;
      if (this.#focus === null) return true;
      const x = tile.origin.x + descriptor.position.x;
      const z = tile.origin.z + descriptor.position.z;
      return (x - this.#focus.x) ** 2 + (z - this.#focus.z) ** 2 <=
        visualRadiusSquared;
    });
  }

  #rebuild(): void {
    const visualRadiusSquared =
      MAPPED_ROAD_VEHICLE_STREAMING.visualActivationRadiusM ** 2;
    const descriptors = [...this.#tiles.values()].flatMap((tile) =>
      this.#focus === null && tile.lod !== 0
        ? []
        : tile.descriptors.flatMap((descriptor) => {
          const x = tile.origin.x + descriptor.position.x;
          const z = tile.origin.z + descriptor.position.z;
          return this.#suppressedIds.has(descriptor.id) ||
              (this.#focus !== null &&
                (x - this.#focus.x) ** 2 + (z - this.#focus.z) ** 2 >
                  visualRadiusSquared)
            ? []
            : [{
              descriptor,
              x,
              z,
            }];
        })
    );
    this.#disposeMeshes();
    if (descriptors.length === 0) return;

    const body = createBoxInstances(
      "mapped-parked-bodies",
      descriptors,
      (entry) => {
        const profile = MAPPED_ROAD_VEHICLE_FAMILY_PROFILES[
          entry.descriptor.family
        ];
        return {
          x: 0,
          y: 0.48,
          z: 0,
          width: profile.bodySizeM.x,
          height: 0.62,
          length: profile.bodySizeM.z,
          color: entry.descriptor.bodyColorHex,
        };
      },
      { color: 0xffffff, roughness: 0.58, metalness: 0.12 },
    );
    const cabin = createBoxInstances(
      "mapped-parked-cabins",
      descriptors,
      (entry) => {
        const profile = MAPPED_ROAD_VEHICLE_FAMILY_PROFILES[
          entry.descriptor.family
        ];
        return {
          x: 0,
          y: 0.91 + profile.cabinHeightM * 0.5,
          z: profile.cabinOffsetZ,
          width: profile.bodySizeM.x * 0.82,
          height: profile.cabinHeightM,
          length: profile.bodySizeM.z * profile.cabinLengthScale,
          color: 0x465a61,
        };
      },
      { color: 0xffffff, roughness: 0.5, metalness: 0.02 },
      createUnitTrapezoidCabinGeometry(),
    );
    const roof = createBoxInstances(
      "mapped-parked-roofs",
      descriptors,
      (entry) => {
        const profile = MAPPED_ROAD_VEHICLE_FAMILY_PROFILES[
          entry.descriptor.family
        ];
        return {
          x: 0,
          y: 0.94 + profile.cabinHeightM,
          z: profile.cabinOffsetZ,
          width: profile.bodySizeM.x * 0.78,
          height: 0.09,
          length: profile.bodySizeM.z * profile.roofLengthScale,
          color: entry.descriptor.bodyColorHex,
        };
      },
      { color: 0xffffff, roughness: 0.58, metalness: 0.12 },
    );
    const wheels = createBoxInstances(
      "mapped-parked-wheels",
      descriptors,
      (entry) => {
        const profile = MAPPED_ROAD_VEHICLE_FAMILY_PROFILES[
          entry.descriptor.family
        ];
        return {
          x: 0,
          y: 0,
          z: 0,
          width: profile.bodySizeM.x,
          height: profile.wheelRadiusM * 2,
          length: profile.wheelbaseM,
          color: 0x202322,
        };
      },
      { color: 0xffffff, roughness: 0.92, metalness: 0.01 },
      createUnitWheelSetGeometry(),
    );
    this.#meshes = [body, cabin, roof, wheels];
    this.object.add(...this.#meshes);
  }

  #disposeMeshes(): void {
    for (const mesh of this.#meshes) {
      this.object.remove(mesh);
      mesh.geometry.dispose();
      mesh.material.dispose();
    }
    this.#meshes = [];
  }
}

interface InstanceBox {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly width: number;
  readonly height: number;
  readonly length: number;
  readonly color: number;
}

function createBoxInstances(
  name: string,
  descriptors: readonly WorldParkedVehicleDescriptor[],
  boxFor: (descriptor: WorldParkedVehicleDescriptor) => InstanceBox,
  materialParameters: ConstructorParameters<typeof MeshStandardMaterial>[0],
  geometry: BufferGeometry = new BoxGeometry(1, 1, 1),
): InstancedMesh<BufferGeometry, MeshStandardMaterial> {
  const material = new MeshStandardMaterial(materialParameters);
  const mesh = new InstancedMesh(geometry, material, descriptors.length);
  mesh.name = name;
  mesh.instanceMatrix.setUsage(DynamicDrawUsage);
  mesh.castShadow = false;
  mesh.receiveShadow = true;
  const transform = new Object3D();
  const color = new Color();
  descriptors.forEach((entry, index) => {
    const box = boxFor(entry);
    transform.position.set(
      entry.x + Math.cos(entry.descriptor.yawRadians) * box.x +
        Math.sin(entry.descriptor.yawRadians) * box.z,
      entry.descriptor.groundElevationM + box.y,
      entry.z - Math.sin(entry.descriptor.yawRadians) * box.x +
        Math.cos(entry.descriptor.yawRadians) * box.z,
    );
    transform.rotation.set(
      entry.descriptor.pitchRadians,
      entry.descriptor.yawRadians,
      0,
      "YXZ",
    );
    transform.scale.set(box.width, box.height, box.length);
    transform.updateMatrix();
    mesh.setMatrixAt(index, transform.matrix);
    mesh.setColorAt(index, color.setHex(box.color));
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor !== null) mesh.instanceColor.needsUpdate = true;
  return mesh;
}

function createUnitTrapezoidCabinGeometry(): BufferGeometry {
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new Float32BufferAttribute([
    -0.5, -0.5, -0.5,
    0.5, -0.5, -0.5,
    0.5, -0.5, 0.5,
    -0.5, -0.5, 0.5,
    -0.41, 0.5, -0.38,
    0.41, 0.5, -0.38,
    0.41, 0.5, 0.38,
    -0.41, 0.5, 0.38,
  ], 3));
  geometry.setIndex([
    4, 6, 5, 4, 7, 6,
    3, 2, 6, 3, 6, 7,
    1, 0, 4, 1, 4, 5,
    2, 1, 5, 2, 5, 6,
    0, 3, 7, 0, 7, 4,
  ]);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function createUnitWheelSetGeometry(): BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  for (const x of [-0.47, 0.47]) {
    for (const z of [-0.5, 0.5]) {
      appendUnitBox(positions, indices, x, 0.5, z, 0.13, 1, 0.23);
    }
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function appendUnitBox(
  positions: number[],
  indices: number[],
  centreX: number,
  centreY: number,
  centreZ: number,
  width: number,
  height: number,
  length: number,
): void {
  const offset = positions.length / 3;
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const halfLength = length / 2;
  positions.push(
    centreX - halfWidth, centreY - halfHeight, centreZ - halfLength,
    centreX + halfWidth, centreY - halfHeight, centreZ - halfLength,
    centreX + halfWidth, centreY + halfHeight, centreZ - halfLength,
    centreX - halfWidth, centreY + halfHeight, centreZ - halfLength,
    centreX - halfWidth, centreY - halfHeight, centreZ + halfLength,
    centreX + halfWidth, centreY - halfHeight, centreZ + halfLength,
    centreX + halfWidth, centreY + halfHeight, centreZ + halfLength,
    centreX - halfWidth, centreY + halfHeight, centreZ + halfLength,
  );
  for (const index of [
    0, 2, 1, 0, 3, 2,
    4, 5, 6, 4, 6, 7,
    0, 1, 5, 0, 5, 4,
    3, 7, 6, 3, 6, 2,
    1, 2, 6, 1, 6, 5,
    0, 4, 7, 0, 7, 3,
  ]) {
    indices.push(offset + index);
  }
}

function sameIds(left: ReadonlySet<string>, right: ReadonlySet<string>): boolean {
  if (left.size !== right.size) return false;
  return [...left].every((id) => right.has(id));
}
